import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Card,
  Select,
  Space,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  message,
  Tag,
  Table,
  Typography,
  ColorPicker,
  Divider,
  Spin,
  Calendar,
  Checkbox,
} from 'antd';
import { ReloadOutlined, PlusOutlined, ApartmentOutlined, SearchOutlined, DeleteOutlined, BgColorsOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { useLanguage } from '../contexts/LanguageContext';
import './CalendarPage.css';

dayjs.extend(utc);

const { Title } = Typography;

const VIEW_OPTIONS = [
  { value: 'timeGridDay', i18n: 'calendar.viewDay', fallback: 'Day' },
  { value: 'timeGridWeek', i18n: 'calendar.viewWeek', fallback: 'Week' },
  { value: 'dayGridMonth', i18n: 'calendar.viewMonth', fallback: 'Month' },
  { value: 'multiMonthYear', i18n: 'calendar.viewYear', fallback: 'Year' },
];

const ALL_FILTER = '__ALL__';
const UNTYPED_FILTER = '__UNTYPED__';
const UNTYPED_COLOR = '#9aa0a6';
const DEFAULT_TYPE_COLORS = [
  '#4285f4', '#7986cb', '#9e69af', '#33b679', '#f4511e',
  '#e67c73', '#f6bf26', '#0b8043', '#3f51b5', '#8e24aa',
];

const STATUS_COLORS = {
  tentative: '#faad14',
  confirmed: '#52c41a',
  cancelled: '#8c8c8c',
};

const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.tentative;

const getEventBlockColor = (eventTypeColor) => eventTypeColor || UNTYPED_COLOR;

const toHexColor = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value?.toHexString ? value.toHexString() : null;
};

const renderStatusOption = (status, label) => (
  <span className="calendar-status-option">
    <span className="calendar-status-dot" style={{ backgroundColor: getStatusColor(status) }} />
    {label}
  </span>
);

const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  return fetch(url, { ...options, headers });
};

const CalendarPage = () => {
  const { t } = useLanguage();
  const calendarRef = useRef(null);

  const [view, setView] = useState('dayGridMonth');
  const [workflowFilter, setWorkflowFilter] = useState(ALL_FILTER);
  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  const [resourceContacts, setResourceContacts] = useState([]);
  const [calendarEventTypes, setCalendarEventTypes] = useState([]);
  const [visibleTypeKeys, setVisibleTypeKeys] = useState(new Set());
  const [sidebarDate, setSidebarDate] = useState(() => dayjs());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null); // null = creating
  const [form] = Form.useForm();
  const selectedResourceContactIds = Form.useWatch('resourceContactIds', form) || [];
  const watchedEventType = Form.useWatch('eventType', form) || [];
  const [submitting, setSubmitting] = useState(false);
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);
  const [resourcePickerSearch, setResourcePickerSearch] = useState('');
  const [resourcePickerGroupFilter, setResourcePickerGroupFilter] = useState(null);
  const [resourcePickerTagFilter, setResourcePickerTagFilter] = useState(null);

  const [visibleRange, setVisibleRange] = useState({
    from: dayjs().startOf('month').subtract(1, 'week').toISOString(),
    to: dayjs().endOf('month').add(1, 'week').toISOString(),
  });

  const loadWorkflowDefinitions = useCallback(async () => {
    try {
      const r = await authFetch('/api/calendar/workflow-definitions');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setWorkflowDefinitions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('[Calendar] 載入流程定義失敗', e);
    }
  }, []);

  const loadResourceContacts = useCallback(async () => {
    try {
      const r = await authFetch('/api/calendar/resource-contacts?take=500');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setResourceContacts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('[Calendar] 載入資源 contact 失敗', e);
    }
  }, []);

  const loadCalendarEventTypes = useCallback(async () => {
    try {
      const r = await authFetch('/api/calendar/event-types');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCalendarEventTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('[Calendar] 載入日曆類型失敗', e);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', visibleRange.from);
      params.set('to', visibleRange.to);
      if (workflowFilter && workflowFilter !== ALL_FILTER) {
        params.set('workflowDefinitionId', String(workflowFilter));
      }
      const r = await authFetch(`/api/calendar/events?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const mapped = (Array.isArray(data) ? data : []).map((e) => {
        const blockColor = getEventBlockColor(e.eventTypeColor);
        return {
        id: e.id,
        title: e.title,
        start: e.startUtc,
        end: e.endUtc,
        allDay: e.isAllDay,
        backgroundColor: blockColor,
        borderColor: blockColor,
        extendedProps: {
          description: e.description,
          location: e.location,
          status: e.status,
          color: e.color,
          timeZone: e.timeZone,
          workflowDefinitionId: e.workflowDefinitionId,
          workflowDefinitionName: e.workflowDefinitionName,
          workflowExecutionId: e.workflowExecutionId,
          batchId: e.batchId,
          externalRef: e.externalRef,
          sourceKind: e.sourceKind,
          metadata: e.metadata,
          resourceContactIds: e.resourceContactIds || [],
          resources: e.resources || [],
          eventTypeId: e.eventTypeId,
          eventTypeName: e.eventTypeName,
          eventTypeColor: e.eventTypeColor,
        },
      };
      });
      setEvents(mapped);
    } catch (e) {
      console.error('[Calendar] 載入事件失敗', e);
      message.error(t('calendar.loadFail') || '載入事件失敗');
    } finally {
      setLoading(false);
    }
  }, [visibleRange.from, visibleRange.to, workflowFilter, t]);

  useEffect(() => { loadWorkflowDefinitions(); loadResourceContacts(); loadCalendarEventTypes(); }, [loadWorkflowDefinitions, loadResourceContacts, loadCalendarEventTypes]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    setVisibleTypeKeys((prev) => {
      const next = new Set(prev);
      let changed = false;
      calendarEventTypes.forEach((typeRow) => {
        if (!next.has(typeRow.id)) {
          next.add(typeRow.id);
          changed = true;
        }
      });
      if (!next.has(UNTYPED_FILTER)) {
        next.add(UNTYPED_FILTER);
        changed = true;
      }
      if (prev.size === 0 && calendarEventTypes.length > 0) {
        return new Set([...calendarEventTypes.map((typeRow) => typeRow.id), UNTYPED_FILTER]);
      }
      return changed ? next : prev;
    });
  }, [calendarEventTypes]);

  const visibleEvents = useMemo(() => (
    events.filter((ev) => {
      const typeId = ev.extendedProps?.eventTypeId;
      if (!typeId) return visibleTypeKeys.has(UNTYPED_FILTER);
      return visibleTypeKeys.has(typeId);
    })
  ), [events, visibleTypeKeys]);

  const handleDatesSet = useCallback((arg) => {
    setVisibleRange({
      from: dayjs(arg.start).toISOString(),
      to: dayjs(arg.end).toISOString(),
    });
    const mid = dayjs(arg.start).add(dayjs(arg.end).diff(dayjs(arg.start), 'day') / 2, 'day');
    setSidebarDate(mid);
  }, []);

  const handleSidebarDateSelect = useCallback((date) => {
    if (!date) return;
    setSidebarDate(date);
    const api = calendarRef.current?.getApi();
    if (api) api.gotoDate(date.toDate());
  }, []);

  const handleViewChange = useCallback((nextView) => {
    setView(nextView);
    const api = calendarRef.current?.getApi();
    if (api) api.changeView(nextView);
  }, []);

  const toggleTypeVisibility = useCallback((typeKey, checked) => {
    setVisibleTypeKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(typeKey);
      else next.delete(typeKey);
      return next;
    });
  }, []);

  // ---------- editor ----------
  const openCreate = useCallback((dateInfo) => {
    setEditingEvent(null);
    form.resetFields();
    const start = dateInfo?.startStr ? dayjs(dateInfo.startStr) : dayjs();
    const end = dateInfo?.endStr ? dayjs(dateInfo.endStr) : start.add(1, 'hour');
    form.setFieldsValue({
      title: '',
      isAllDay: !!dateInfo?.allDay,
      timeRange: [start, end],
      workflowDefinitionId:
        workflowFilter && workflowFilter !== ALL_FILTER ? Number(workflowFilter) : null,
      status: 'tentative',
      resourceContactIds: [],
      eventType: [],
      eventTypeColor: DEFAULT_TYPE_COLORS[0],
      location: '',
      description: '',
    });
    setEditorOpen(true);
    loadCalendarEventTypes();
  }, [form, workflowFilter, loadCalendarEventTypes]);

  const openEdit = useCallback((ev) => {
    setEditingEvent(ev);
    const start = dayjs(ev.start);
    const end = ev.end ? dayjs(ev.end) : start.add(1, 'hour');
    form.setFieldsValue({
      title: ev.title || '',
      isAllDay: !!ev.allDay,
      timeRange: [start, end],
      workflowDefinitionId: ev.extendedProps?.workflowDefinitionId || null,
      status: ev.extendedProps?.status || 'tentative',
      resourceContactIds: ev.extendedProps?.resourceContactIds || [],
      eventType: ev.extendedProps?.eventTypeName ? [ev.extendedProps.eventTypeName] : [],
      eventTypeColor: ev.extendedProps?.eventTypeColor || DEFAULT_TYPE_COLORS[0],
      location: ev.extendedProps?.location || '',
      description: ev.extendedProps?.description || '',
    });
    setEditorOpen(true);
    loadCalendarEventTypes();
  }, [form, loadCalendarEventTypes]);

  const handleEventClick = useCallback((info) => {
    info.jsEvent?.preventDefault?.();
    openEdit({
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      allDay: info.event.allDay,
      extendedProps: info.event.extendedProps,
    });
  }, [openEdit]);

  const handleEventDrop = useCallback(async (info) => {
    try {
      const id = info.event.id;
      const startUtc = info.event.start ? dayjs(info.event.start).utc().toISOString() : null;
      const endUtc = info.event.end ? dayjs(info.event.end).utc().toISOString()
        : (startUtc ? dayjs(startUtc).add(1, 'hour').toISOString() : null);
      const body = { startUtc, endUtc, isAllDay: info.event.allDay };
      const r = await authFetch(`/api/calendar/events/${id}`, {
        method: 'PUT', body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      message.success(t('calendar.saved') || '已更新');
    } catch (e) {
      message.error(e.message || (t('calendar.saveFail') || '更新失敗'));
      info.revert();
    }
  }, [t]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const [start, end] = values.timeRange || [];
      const eventTypeName = (Array.isArray(values.eventType) ? values.eventType[0] : values.eventType) || '';
      const matchedType = calendarEventTypes.find((typeRow) => typeRow.name === eventTypeName);
      const payload = {
        title: values.title,
        description: values.description || null,
        location: values.location || null,
        status: values.status || 'tentative',
        startUtc: start ? start.utc().toISOString() : null,
        endUtc: end ? end.utc().toISOString() : null,
        isAllDay: !!values.isAllDay,
        timeZone: dayjs.tz?.guess?.() || Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        workflowDefinitionId: values.workflowDefinitionId || null,
        resourceContactIds: values.resourceContactIds || [],
        eventTypeName,
        eventTypeColor: matchedType?.color || toHexColor(values.eventTypeColor) || null,
      };

      let r;
      if (editingEvent && editingEvent.id) {
        r = await authFetch(`/api/calendar/events/${editingEvent.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
      } else {
        r = await authFetch('/api/calendar/events', {
          method: 'POST', body: JSON.stringify(payload),
        });
      }
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${r.status}`);
      }
      message.success(t('calendar.saved') || '已儲存');
      setEditorOpen(false);
      loadEvents();
      loadCalendarEventTypes();
    } catch (e) {
      if (e?.errorFields) return; // form validation
      console.error(e);
      message.error(e.message || (t('calendar.saveFail') || '儲存失敗'));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!editingEvent || !editingEvent.id) return;
    Modal.confirm({
      title: t('calendar.confirmDelete') || '確認刪除此事件？',
      onOk: async () => {
        try {
          const r = await authFetch(`/api/calendar/events/${editingEvent.id}`, { method: 'DELETE' });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          message.success(t('calendar.deleted') || '已刪除');
          setEditorOpen(false);
          loadEvents();
        } catch (e) {
          message.error(e.message || (t('calendar.deleteFail') || '刪除失敗'));
        }
      },
    });
  };

  const workflowMap = useMemo(() => {
    const map = new Map();
    workflowDefinitions.forEach((w) => map.set(w.id, w.name));
    return map;
  }, [workflowDefinitions]);

  const resourceContactMap = useMemo(() => {
    const map = new Map();
    resourceContacts.forEach((c) => map.set(c.id, c));
    return map;
  }, [resourceContacts]);

  const selectedResourceContacts = useMemo(() => (
    selectedResourceContactIds
      .map((id) => resourceContactMap.get(id))
      .filter(Boolean)
  ), [selectedResourceContactIds, resourceContactMap]);

  const allGroups = useMemo(() => {
    const seen = new Map();
    resourceContacts.forEach((c) => {
      if (c.broadcastGroupId && c.broadcastGroupName && !seen.has(c.broadcastGroupId)) {
        seen.set(c.broadcastGroupId, c.broadcastGroupName);
      }
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [resourceContacts]);

  const allTags = useMemo(() => {
    const set = new Set();
    resourceContacts.forEach((c) => {
      if (c.hashtags) {
        c.hashtags.split(',').map((t) => t.trim()).filter(Boolean).forEach((t) => set.add(t));
      }
    });
    return Array.from(set).sort().map((t) => ({ value: t, label: t }));
  }, [resourceContacts]);

  const filteredResourceContacts = useMemo(() => {
    let list = resourceContacts;
    const keyword = resourcePickerSearch.trim().toLowerCase();
    if (keyword) {
      list = list.filter((c) => [
        c.name,
        c.position,
        c.department,
        c.companyName,
        c.whatsAppNumber,
        c.email,
        c.occupation,
        c.title,
        c.hashtags,
        c.broadcastGroupName,
      ].filter(Boolean).join(' ').toLowerCase().includes(keyword));
    }
    if (resourcePickerGroupFilter) {
      list = list.filter((c) => c.broadcastGroupId === resourcePickerGroupFilter);
    }
    if (resourcePickerTagFilter) {
      list = list.filter((c) => {
        if (!c.hashtags) return false;
        return c.hashtags.split(',').map((t) => t.trim().toLowerCase()).includes(resourcePickerTagFilter.toLowerCase());
      });
    }
    return list;
  }, [resourceContacts, resourcePickerSearch, resourcePickerGroupFilter, resourcePickerTagFilter]);

  const resourceColumns = useMemo(() => [
    {
      title: t('calendar.resourceName') || 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (v) => <b>{v}</b>,
    },
    {
      title: t('calendar.resourcePosition') || 'Position / Department',
      key: 'position',
      width: 160,
      render: (_, r) => [r.position, r.department].filter(Boolean).join(' / ') || '—',
    },
    {
      title: t('calendar.resourceCompany') || 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 130,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: t('calendar.resourcePhone') || 'Phone',
      dataIndex: 'whatsAppNumber',
      key: 'whatsAppNumber',
      width: 120,
      render: (v) => v || '—',
    },
    {
      title: t('calendar.resourceEmail') || 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: t('calendar.resourceGroup') || 'Group',
      dataIndex: 'broadcastGroupName',
      key: 'broadcastGroupName',
      width: 130,
      ellipsis: true,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    {
      title: t('calendar.resourceTags') || 'Tags',
      dataIndex: 'hashtags',
      key: 'hashtags',
      width: 200,
      render: (v) => {
        if (!v) return '—';
        const tags = v.split(',').map((t) => t.trim()).filter(Boolean);
        if (tags.length === 0) return '—';
        return (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {tags.map((tag) => <Tag key={tag} style={{ margin: 0 }}>{tag}</Tag>)}
          </span>
        );
      },
    },
  ], [t]);

  const removeResourceContact = useCallback((id) => {
    const next = selectedResourceContactIds.filter((x) => x !== id);
    form.setFieldsValue({ resourceContactIds: next });
  }, [form, selectedResourceContactIds]);

  const calendarEventTypeOptions = useMemo(() => (
    calendarEventTypes.map((typeRow) => ({
      value: typeRow.name,
      label: typeRow.name,
      typeId: typeRow.id,
      color: typeRow.color,
    }))
  ), [calendarEventTypes]);

  const selectedEventTypeName = Array.isArray(watchedEventType) ? watchedEventType[0] : watchedEventType;
  const matchedEventType = useMemo(() => (
    calendarEventTypes.find((typeRow) => typeRow.name === selectedEventTypeName)
  ), [calendarEventTypes, selectedEventTypeName]);
  const isNewEventType = !!selectedEventTypeName && !matchedEventType;

  const handleUpdateEventTypeColor = useCallback(async (typeId, color) => {
    const hex = toHexColor(color);
    if (!typeId || !hex) return;
    try {
      const r = await authFetch(`/api/calendar/event-types/${typeId}`, {
        method: 'PUT',
        body: JSON.stringify({ color: hex }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${r.status}`);
      }
      loadCalendarEventTypes();
      loadEvents();
    } catch (err) {
      message.error(err.message || (t('calendar.eventTypeColorUpdateFail') || '更新日曆類型色彩失敗'));
    }
  }, [loadCalendarEventTypes, loadEvents, t]);

  const handleDeleteEventType = useCallback(async (typeId, typeName, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!typeId) return;
    try {
      const r = await authFetch(`/api/calendar/event-types/${typeId}`, { method: 'DELETE' });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        const count = errBody.count;
        const fallback = t('calendar.eventTypeDeleteInUse') || '此日曆類型仍有事件使用中，無法刪除';
        message.error(
          count != null
            ? (t('calendar.eventTypeDeleteInUseCount') || fallback).replace('{count}', String(count))
            : (errBody.error || fallback)
        );
        return;
      }
      message.success(t('calendar.eventTypeDeleted') || '已刪除日曆類型');
      const currentType = form.getFieldValue('eventType');
      const currentName = Array.isArray(currentType) ? currentType[0] : currentType;
      if (currentName === typeName) {
        form.setFieldsValue({ eventType: [] });
      }
      loadCalendarEventTypes();
    } catch (err) {
      message.error(err.message || (t('calendar.eventTypeDeleteFail') || '刪除日曆類型失敗'));
    }
  }, [form, loadCalendarEventTypes, t]);

  const renderCalendarEventTypeOption = useCallback((option) => (
    <div className="calendar-event-type-option">
      <span
        className="calendar-type-color-swatch calendar-type-color-swatch--inline"
        style={{ backgroundColor: option.data?.color || UNTYPED_COLOR }}
      />
      <span className="calendar-event-type-option-label">{option.label}</span>
      {option.data?.typeId ? (
        <Button
          type="text"
          size="small"
          className="calendar-event-type-option-delete"
          icon={<DeleteOutlined />}
          onClick={(evt) => handleDeleteEventType(option.data.typeId, option.value, evt)}
        />
      ) : null}
    </div>
  ), [handleDeleteEventType]);

  const renderCalendarEventContent = useCallback((arg) => {
    const resources = arg.event.extendedProps?.resources || [];
    const resourceNames = resources.map((r) => r.name).filter(Boolean);
    const status = arg.event.extendedProps?.status || 'tentative';
    return (
      <div className="calendar-event-content">
        <span
          className="calendar-event-status-dot"
          style={{ backgroundColor: getStatusColor(status) }}
          title={status}
        />
        {arg.timeText ? <span className="calendar-event-time">{arg.timeText}</span> : null}
        <span className="calendar-event-title">{arg.event.title}</span>
        {resourceNames.length > 0 ? (
          <span className="calendar-event-resources">
            {resourceNames.join(', ')}
          </span>
        ) : null}
      </div>
    );
  }, []);

  return (
    <div className="calendar-page">
      <div className={`calendar-page-layout${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
        <aside className="calendar-sidebar">
          <Button
            type="primary"
            block
            icon={<PlusOutlined />}
            className="calendar-sidebar-create-btn"
            onClick={() => openCreate()}
          >
            {t('calendar.newEvent') || 'New Event'}
          </Button>

          <Calendar
            fullscreen={false}
            className="calendar-sidebar-mini"
            value={sidebarDate}
            onPanelChange={(date) => setSidebarDate(date)}
            onSelect={handleSidebarDateSelect}
          />

          <div className="calendar-sidebar-section">
            <div className="calendar-sidebar-section-title">
              {t('calendar.sidebarEventTypes') || '日曆類型'}
            </div>
            <div className="calendar-type-filter-list">
              {calendarEventTypes.map((typeRow, index) => {
                const typeColor = typeRow.color || DEFAULT_TYPE_COLORS[index % DEFAULT_TYPE_COLORS.length];
                return (
                  <div key={typeRow.id} className="calendar-type-filter-row">
                    <Checkbox
                      checked={visibleTypeKeys.has(typeRow.id)}
                      onChange={(e) => toggleTypeVisibility(typeRow.id, e.target.checked)}
                      className="calendar-type-checkbox"
                      style={{ '--type-color': typeColor }}
                    />
                    <span
                      className="calendar-type-filter-label"
                      onClick={() => toggleTypeVisibility(typeRow.id, !visibleTypeKeys.has(typeRow.id))}
                    >
                      {typeRow.name}
                    </span>
                    <ColorPicker
                      size="small"
                      format="hex"
                      value={typeColor}
                      onChangeComplete={(c) => handleUpdateEventTypeColor(typeRow.id, c)}
                    >
                      <Button
                        type="text"
                        size="small"
                        className="calendar-type-color-edit"
                        icon={<BgColorsOutlined />}
                        title={t('calendar.editTypeColor') || '設定類型色彩'}
                      />
                    </ColorPicker>
                  </div>
                );
              })}
              <div className="calendar-type-filter-row">
                <Checkbox
                  checked={visibleTypeKeys.has(UNTYPED_FILTER)}
                  onChange={(e) => toggleTypeVisibility(UNTYPED_FILTER, e.target.checked)}
                  className="calendar-type-checkbox"
                  style={{ '--type-color': UNTYPED_COLOR }}
                />
                <span
                  className="calendar-type-filter-label"
                  onClick={() => toggleTypeVisibility(UNTYPED_FILTER, !visibleTypeKeys.has(UNTYPED_FILTER))}
                >
                  {t('calendar.untypedEvents') || '未分類'}
                </span>
              </div>
              {calendarEventTypes.length === 0 ? (
                <div className="calendar-sidebar-empty-hint">
                  {t('calendar.noEventTypesYet') || '尚無日曆類型，新增事件時可輸入建立'}
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        <div className="calendar-main-wrap">
          <Button
            type="text"
            className="calendar-sidebar-toggle"
            icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed
              ? (t('calendar.expandSidebar') || '展開側邊欄')
              : (t('calendar.collapseSidebar') || '收合側邊欄')}
          />

      <Card
        bordered={false}
        className="calendar-page-card"
        title={
          <Space size="middle">
            <Title level={4} style={{ margin: 0 }}>
              {t('calendar.title') || '日曆'}
            </Title>
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {workflowFilter === ALL_FILTER
                ? (t('calendar.filterAll') || 'All Workflows')
                : (workflowMap.get(Number(workflowFilter)) || `#${workflowFilter}`)}
            </Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              value={view}
              onChange={handleViewChange}
              style={{ width: 140 }}
              options={VIEW_OPTIONS.map(v => ({
                value: v.value,
                label: t(v.i18n) || v.fallback,
              }))}
            />
            <Select
              value={workflowFilter}
              style={{ width: 220 }}
              onChange={setWorkflowFilter}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              options={[
                { value: ALL_FILTER, label: t('calendar.filterAll') || 'All Workflows' },
                ...workflowDefinitions.map(w => ({
                  value: w.id,
                  label: (
                    <span>
                      <ApartmentOutlined /> {w.name || `#${w.id}`}
                    </span>
                  ),
                })),
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={loadEvents}>
              {t('calendar.refresh') || 'Refresh'}
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading} wrapperClassName="calendar-spin-wrapper">
          <div className="calendar-fullcalendar-wrap">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
              initialView={view}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: '',
              }}
              buttonText={{
                today: t('calendar.today') || 'Today',
              }}
              firstDay={0}
              height="100%"
              expandRows
              events={visibleEvents}
              editable
              selectable
              selectMirror
              dayMaxEvents={5}
              datesSet={handleDatesSet}
              select={openCreate}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventDrop}
              eventContent={renderCalendarEventContent}
              eventDidMount={(arg) => {
                const resources = arg.event.extendedProps?.resources || [];
                const resourceNames = resources.map(r => r.name).filter(Boolean).join(', ');
                arg.el.setAttribute('title', resourceNames
                  ? `${arg.event.title}\n${t('calendar.fieldResources') || 'Resources'}: ${resourceNames}`
                  : arg.event.title);
              }}
            />
          </div>
        </Spin>
      </Card>
        </div>
      </div>

      <Modal
        className="calendar-event-modal"
        title={editingEvent
          ? (t('calendar.editEvent') || 'Edit Event')
          : (t('calendar.newEvent') || 'New Event')}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={submit}
        confirmLoading={submitting}
        width={920}
        footer={[
          editingEvent ? (
            <Button danger key="del" onClick={remove}>
              {t('calendar.delete') || 'Delete'}
            </Button>
          ) : null,
          <Button key="cancel" onClick={() => setEditorOpen(false)}>
            {t('calendar.cancel') || 'Cancel'}
          </Button>,
          <Button key="ok" type="primary" loading={submitting} onClick={submit}>
            {t('calendar.save') || 'Save'}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" className="calendar-event-form">
          <Form.Item
            className="calendar-event-form-full"
            name="title"
            label={t('calendar.fieldTitle') || 'Title'}
            rules={[{ required: true, message: t('calendar.requireTitle') || 'Title is required' }]}
          >
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item
            className="calendar-event-time-range"
            name="timeRange"
            label={t('calendar.fieldTime') || 'Start / End'}
            rules={[{ required: true, message: t('calendar.requireTime') || 'Time is required' }]}
          >
            <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isAllDay" label={t('calendar.fieldAllDay') || 'All Day'} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="status" label={t('calendar.fieldStatus') || 'Status'} initialValue="tentative">
            <Select options={[
              { value: 'tentative', label: renderStatusOption('tentative', t('calendar.statusTentative') || 'Tentative') },
              { value: 'confirmed', label: renderStatusOption('confirmed', t('calendar.statusConfirmed') || 'Confirmed') },
              { value: 'cancelled', label: renderStatusOption('cancelled', t('calendar.statusCancelled') || 'Cancelled') },
            ]} />
          </Form.Item>
          <div className="calendar-event-type-field calendar-event-form-full">
            <Form.Item
              name="eventType"
              label={t('calendar.fieldEventType') || '日曆類型'}
              className="calendar-event-type-select-item"
            >
              <Select
                mode="tags"
                maxCount={1}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('calendar.fieldEventTypePlaceholder') || '選擇或輸入日曆類型'}
                options={calendarEventTypeOptions}
                optionRender={renderCalendarEventTypeOption}
              />
            </Form.Item>
            {matchedEventType ? (
              <div className="calendar-event-type-color-preview">
                <span className="calendar-event-type-color-label">{t('calendar.fieldTypeColor') || '類型色彩'}</span>
                <span
                  className="calendar-type-color-swatch calendar-type-color-swatch--large"
                  style={{ backgroundColor: matchedEventType.color || UNTYPED_COLOR }}
                  title={t('calendar.typeColorSidebarHint') || '可在左側選單調整類型色彩'}
                />
              </div>
            ) : isNewEventType ? (
              <Form.Item
                name="eventTypeColor"
                label={t('calendar.fieldTypeColor') || '類型色彩'}
                className="calendar-event-type-color-item"
              >
                <ColorPicker allowClear={false} format="hex" />
              </Form.Item>
            ) : null}
          </div>
          <Form.Item
            className="calendar-event-workflow"
            name="workflowDefinitionId"
            label={t('calendar.fieldWorkflow') || 'Workflow Definition'}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('calendar.fieldWorkflowPlaceholder') || 'Optional'}
              options={workflowDefinitions.map(w => ({ value: w.id, label: w.name || `#${w.id}` }))}
            />
          </Form.Item>
          <Form.Item
            className="calendar-event-location"
            name="location"
            label={t('calendar.fieldLocation') || 'Location'}
          >
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="resourceContactIds" hidden>
            <Select mode="multiple" />
          </Form.Item>
          <Form.Item
            className="calendar-event-form-full"
            label={t('calendar.fieldResources') || 'Resources'}
          >
            <div className="calendar-resource-picker-field">
              <Button onClick={() => {
                setResourcePickerSearch('');
                setResourcePickerGroupFilter(null);
                setResourcePickerTagFilter(null);
                setResourcePickerOpen(true);
              }}>
                {t('calendar.selectResources') || 'Select resources'} ({selectedResourceContactIds.length})
              </Button>
              <div className="calendar-resource-tags">
                {selectedResourceContacts.length === 0 ? (
                  <span className="calendar-resource-empty">
                    {t('calendar.fieldResourcesPlaceholder') || 'Select contacts as resources'}
                  </span>
                ) : selectedResourceContacts.map((c) => (
                  <Tag
                    key={c.id}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      removeResourceContact(c.id);
                    }}
                  >
                    {c.name}
                    {c.whatsAppNumber || c.email ? (
                      <span style={{ color: '#888' }}>
                        {' '}({[c.whatsAppNumber, c.email].filter(Boolean).join(' · ')})
                      </span>
                    ) : null}
                  </Tag>
                ))}
              </div>
            </div>
          </Form.Item>
          <Form.Item
            className="calendar-event-form-full"
            name="description"
            label={t('calendar.fieldDescription') || 'Description'}
          >
            <Input.TextArea rows={8} />
          </Form.Item>

          {editingEvent?.extendedProps?.workflowDefinitionId && (
            <div className="calendar-event-form-full calendar-event-meta">
              <Divider />
              <Space wrap size={12}>
                <span>
                  <ApartmentOutlined />{' '}
                  {t('calendar.fromWorkflow') || 'Workflow'}:{' '}
                  <b>{editingEvent.extendedProps.workflowDefinitionName || `#${editingEvent.extendedProps.workflowDefinitionId}`}</b>
                </span>
                {editingEvent.extendedProps.workflowExecutionId ? (
                  <span style={{ color: '#888' }}>
                    Execution #{editingEvent.extendedProps.workflowExecutionId}
                  </span>
                ) : null}
                {editingEvent.extendedProps.externalRef ? (
                  <span style={{ color: '#888' }}>ref: {editingEvent.extendedProps.externalRef}</span>
                ) : null}
              </Space>
            </div>
          )}
        </Form>
      </Modal>

      <Modal
        title={t('calendar.resourcePickerTitle') || 'Select resources'}
        open={resourcePickerOpen}
        onCancel={() => setResourcePickerOpen(false)}
        width={1100}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setResourcePickerOpen(false)}>
            {t('calendar.cancel') || 'Cancel'}
          </Button>,
          <Button key="apply" type="primary" onClick={() => setResourcePickerOpen(false)}>
            {t('calendar.apply') || 'Apply'} ({selectedResourceContactIds.length})
          </Button>,
        ]}
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search
            allowClear
            prefix={<SearchOutlined />}
            enterButton
            style={{ width: 320 }}
            value={resourcePickerSearch}
            placeholder={t('calendar.resourcePickerSearchPlaceholder') || 'Search name / phone / email / group / tag'}
            onChange={(e) => setResourcePickerSearch(e.target.value)}
            onSearch={(v) => setResourcePickerSearch((v || '').trim())}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('calendar.filterGroup') || 'Group'}
            style={{ width: 180 }}
            value={resourcePickerGroupFilter}
            onChange={(v) => setResourcePickerGroupFilter(v ?? null)}
            options={allGroups}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('calendar.filterTag') || 'Tag'}
            style={{ width: 160 }}
            value={resourcePickerTagFilter}
            onChange={(v) => setResourcePickerTagFilter(v ?? null)}
            options={allTags}
          />
          {(resourcePickerGroupFilter || resourcePickerTagFilter) && (
            <Button
              size="small"
              onClick={() => {
                setResourcePickerGroupFilter(null);
                setResourcePickerTagFilter(null);
              }}
            >
              {t('calendar.clearFilters') || 'Clear filters'}
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={loadResourceContacts}>
            {t('calendar.refresh') || 'Refresh'}
          </Button>
        </Space>
        <Table
          rowKey="id"
          size="small"
          dataSource={filteredResourceContacts}
          columns={resourceColumns}
          rowSelection={{
            selectedRowKeys: selectedResourceContactIds,
            preserveSelectedRowKeys: true,
            onChange: (keys) => form.setFieldsValue({ resourceContactIds: keys }),
          }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 1050, y: 430 }}
        />
      </Modal>
    </div>
  );
};

export default CalendarPage;
